import type { PlaybackProbe } from '../lib/playbackReport.ts';

/**
 * Test odtwarzania jednego nagrania w TEJ przeglądarce, poza przebiegiem lekcji: pobiera plik, dekoduje go w całości (ile jest w nim dźwięku)
 * i odtwarza zwykłym elementem <audio>, mierząc, w której sekundzie przeglądarka kończy. Porównanie obu liczb pokazuje, czy ucina plik,
 * czy przeglądarka (albo to, jak serwer go wysyła). Wywoływać z kliknięcia (autoplay).
 */
export async function probePlayback(url: string, id: string, timeoutMs = 20000): Promise<PlaybackProbe> {
  const res = await fetch(url, { cache: 'no-store' });
  const probe: PlaybackProbe = {
    id,
    status: res.status,
    bytes: 0,
    contentLength: res.headers.get('content-length'),
    acceptRanges: res.headers.get('accept-ranges'),
    contentType: res.headers.get('content-type'),
    decodedSec: null,
    elementDurationSec: null,
    endedAtSec: null,
    playedSec: null,
  };
  if (!res.ok) return probe;
  const bytes = await res.arrayBuffer();
  probe.bytes = bytes.byteLength;

  const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (Ctx) {
    const ac = new Ctx();
    try {
      probe.decodedSec = (await ac.decodeAudioData(bytes.slice(0))).duration;
    } catch {
      probe.decodedSec = null;
    } finally {
      void ac.close();
    }
  }

  const el = new Audio();
  el.preload = 'auto';
  await new Promise<void>((resolve) => {
    const timer = setTimeout(() => {
      probe.problem = `nie skończyło się w ${Math.round(timeoutMs / 1000)} s (stan: ${el.paused ? 'wstrzymane' : 'gra'}, pozycja ${el.currentTime.toFixed(2)} s)`;
      resolve();
    }, timeoutMs);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    el.addEventListener('ended', () => {
      probe.endedAtSec = el.currentTime;
      done();
    });
    el.addEventListener('error', () => {
      probe.problem = `błąd elementu audio (kod ${el.error?.code ?? '?'}${el.error?.message ? `: ${el.error.message}` : ''})`;
      done();
    });
    el.src = url;
    el.play().catch((e: unknown) => {
      probe.problem = `play() odrzucone: ${e instanceof Error ? e.name : String(e)}`;
      done();
    });
  });
  if (Number.isFinite(el.duration)) probe.elementDurationSec = el.duration;
  let played = 0;
  for (let i = 0; i < el.played.length; i++) played += el.played.end(i) - el.played.start(i);
  probe.playedSec = played;
  el.removeAttribute('src');
  el.load();
  return probe;
}
